import React, { useRef, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { AlertCircleIcon } from 'lucide-react';

import LoadingSpinner from 'components/common/loaders/LoadingSpinner';
import {
  Sheet,
  SheetHeader,
  SheetContent,
  SheetFooter,
  SheetClose
} from '@epoch-ui/components/Sheet/sheet';
import { Separator } from '@epoch-ui/components/Separator';
import { Skeleton } from '@epoch-ui/components/Skeleton/skeleton';
import {
  GET_GUEST_DATA_FOR_RSVP_CONTROL_QUERY,
  GetGuestDataForRSVPControlQueryResponse,
  GetGuestDataForRSVPControlQueryVariables
} from 'graphql/queries/rsvp_control/get_guest_for_rsvp_control';
import { GuestType } from 'types/guest.types';
import { UnsavedChangesConfirmationModalProvider } from '@epoch-components/UnsavedChangesConfirmationModal';
import { MultiSessionPlusOneGuest, PlusOneGuest } from 'types/plus-one.types';

import {
  GuestSideSheetHeader,
  GuestSideSheetDistrolistsSection,
  GuestSideSheetCommunitiesSection,
  GuestSideSheetQuestionnaireSection,
  GuestSideSheetSessionsSection,
  GuestSideSheetPlusOnesSection,
  GuestSideSheetGuestActivitySection,
  GuestSideSheetFooter,
  ExternalGuestSideSheetHeader,
  ExternalGuestSideSheetContentSection
} from './Sections';
import { GuestSideSheetContext, useGuestSideSheetContext } from './guestSideSheetContext';
import { PlusOneSideSheet } from './PlusOneSideSheet/PlusOneSideSheet';

interface GuestSideSheetProps extends React.ComponentPropsWithoutRef<typeof Sheet> {
  gid: string | null;
  eventOrSessionId: number;
  handleUpdateRefetch: () => void;
  initialPlusOneId?: number | null;
}

export const GuestSideSheet: React.FC<GuestSideSheetProps> = ({
  gid,
  eventOrSessionId,
  handleUpdateRefetch,
  initialPlusOneId = null,
  ...props
}) => {
  const closeSheetRef = useRef<HTMLButtonElement>(null);
  const closeSheet = (): void => closeSheetRef.current?.click();
  const [selectedPlusOneId, setSelectedPlusOneId] = useState<number | null>(initialPlusOneId);

  const {
    data: { guestDataForRsvpControl: guestData, guestActivity } = {},
    loading,
    error: getGuestError,
    refetch: refetchGuestData
  } = useQuery<GetGuestDataForRSVPControlQueryResponse, GetGuestDataForRSVPControlQueryVariables>(
    GET_GUEST_DATA_FOR_RSVP_CONTROL_QUERY,
    {
      skip: gid === null,
      variables: { gid: gid as string, eventId: Number(eventOrSessionId) },
      fetchPolicy: 'cache-and-network'
    }
  );

  const contextValue = useMemo(() => {
    if (!guestData) return null;
    if (!guestActivity) return null;
    // For some reason, eventOrSessionId and session ids are being returned as strings
    // instead of numbers. Normalizing the values here, but we should fix the API
    // return type at some point
    const { event } = guestData;
    const sessions = guestData.event.sessions || [];
    const normalizedSessions = sessions.map((session) => ({ ...session, id: Number(session.id) }));
    const normalizedGuestData = { ...guestData, event: { ...event, sessions: normalizedSessions } };
    const normalizedEventOrSessionId = Number(eventOrSessionId);

    const selectedPlusOneGuest =
      selectedPlusOneId !== null
        ? (guestData.guest.plusOnes as PlusOneGuest[] | undefined)?.find(
            (po) => po.plusOneId === selectedPlusOneId
          )
        : null;

    const selectedSession = normalizedSessions.find(
      (session) => session.id === normalizedEventOrSessionId
    );

    // Sessions that have plusOneConfig with allowPlusOnes enabled
    const sessionsWithPlusOnes = normalizedSessions.filter(
      (session) => session.plusOneConfig?.allowPlusOnes
    );

    // Parent plus-ones for multi-session events - contains all plus-ones across sessions
    const allParentPlusOnes = (guestData.parentPlusOnes || []) as MultiSessionPlusOneGuest[];

    return {
      eventOrSessionId: normalizedEventOrSessionId,
      guestData: normalizedGuestData,
      guestActivity,
      refetchGuestData,
      handleUpdateRefetch,
      closeSheet,
      selectedPlusOneId,
      setSelectedPlusOneId,
      selectedPlusOneGuest,
      selectedSession,
      sessionsWithPlusOnes,
      allParentPlusOnes
    };
  }, [
    eventOrSessionId,
    guestData,
    guestActivity,
    refetchGuestData,
    handleUpdateRefetch,
    closeSheet,
    selectedPlusOneId
  ]);

  const handleOpenChange = (open: boolean): void => {
    if (!open) {
      setSelectedPlusOneId(null);
    }
    props.onOpenChange?.(open);
  };

  return (
    <UnsavedChangesConfirmationModalProvider>
      <Sheet {...props} onOpenChange={handleOpenChange}>
        <SheetClose className="tw-hidden" ref={closeSheetRef} />
        <SheetContent className="tw-m-2 tw-flex tw-h-[calc(100%-16px)] tw-flex-col tw-gap-6 tw-rounded-3xl sm:tw-max-w-[480px]">
          {getGuestError && !loading && <ErrorState />}
          {loading && !getGuestError && <LoadingState />}
          {!loading && !getGuestError && contextValue && (
            <GuestSideSheetContext.Provider value={contextValue}>
              <GuestSideSheetContent />
            </GuestSideSheetContext.Provider>
          )}
        </SheetContent>
      </Sheet>
    </UnsavedChangesConfirmationModalProvider>
  );
};

const GuestSideSheetContent = (): React.ReactNode => {
  const {
    guestData: {
      guest: { guestType }
    },
    selectedPlusOneId,
    selectedPlusOneGuest
  } = useGuestSideSheetContext();

  // If a plus-one is selected, show the PlusOneSideSheet
  if (selectedPlusOneId !== null && selectedPlusOneGuest) {
    return <PlusOneSideSheet />;
  }

  // Otherwise, show the regular guest side sheet content
  if (!guestType) return <ErrorState />;

  const externalGuestTypes: GuestType[] = [
    GuestType.EXTERNAL_MEET,
    GuestType.EXTERNAL_ZOOM,
    GuestType.EXTERNAL_MEET_WITHOUT_EMAIL,
    GuestType.EXTERNAL_ZOOM_WITHOUT_EMAIL
  ];
  if (guestType === GuestType.EPOCH) {
    return (
      <>
        <GuestSideSheetHeader />
        <div className="tw-flex tw-flex-grow tw-flex-col tw-gap-6 tw-overflow-y-auto">
          <GuestSideSheetDistrolistsSection />
          <GuestSideSheetCommunitiesSection />
          <GuestSideSheetSessionsSection />
          <GuestSideSheetPlusOnesSection />
          <GuestSideSheetQuestionnaireSection />
          <GuestSideSheetGuestActivitySection />
        </div>
        <GuestSideSheetFooter />
      </>
    );
  } else if (externalGuestTypes.includes(guestType)) {
    return (
      <>
        <ExternalGuestSideSheetHeader />
        <div className="tw-flex tw-flex-grow tw-flex-col tw-gap-6 tw-overflow-y-auto">
          <ExternalGuestSideSheetContentSection />
        </div>
      </>
    );
  } else {
    return <ErrorState />;
  }
};

const SkeletonWithChildren: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  return (
    <>
      <SheetHeader className="tw-flex-row tw-gap-4">
        <Skeleton className="tw-h-16 tw-w-16 tw-rounded-lg" />
        <div className="tw-flex tw-flex-grow tw-flex-col tw-items-start tw-gap-1">
          <Skeleton className="tw-h-6 tw-w-3/6" />
          <Skeleton className="tw-h-4 tw-w-5/6" />
        </div>
      </SheetHeader>
      {children}
      <Separator className="-tw-mx-6 tw-w-[unset]" />
      <SheetFooter className="tw-sticky tw-bottom-0 tw-justify-between tw-bg-white">
        <Skeleton className="tw-h-8 tw-w-24" />
        <Skeleton className="tw-h-8 tw-w-24" />
      </SheetFooter>
    </>
  );
};

const LoadingState = (): React.ReactElement => {
  return (
    <SkeletonWithChildren>
      <div className="tw-flex tw-flex-grow tw-flex-col tw-items-center tw-justify-center tw-gap-6">
        <LoadingSpinner />
      </div>
    </SkeletonWithChildren>
  );
};

const ErrorState = (): React.ReactElement => {
  return (
    <SkeletonWithChildren>
      <div className="tw-flex tw-h-full tw-flex-col tw-items-center tw-justify-center tw-gap-3">
        <AlertCircleIcon size={24} className="tw-text-destructive-foreground" />
        <p className="tw-m-0 tw-text-base tw-font-medium tw-leading-5 tw-text-destructive-foreground">
          Error while loading guest details
        </p>
      </div>
    </SkeletonWithChildren>
  );
};
